import React, { useState, useEffect } from 'react';
import './AssignJobModal.css';

// Bottom-sheet modal for a Company SP to route a job to one of their on-staff
// individual providers — the Jobs → Requests tab's "Assign" card action, and
// (mode="reassign") the "Re-Assign" action for a PENDING job already routed
// to an inspector who hasn't accepted yet. Mirrors
// transaction/AssignServiceProviderModal.js's shape: pure presentation +
// local selection state, network calls (vendor roster fetch, PATCH
// assign-inspector/reassign-inspector) owned by the parent page
// (JobsPage.js) and passed in as props.
const AssignJobModal = ({
  visible,
  job,
  vendors,
  vendorsLoading,
  vendorsError,
  onClose,
  onAssign,
  mode = 'assign',
}) => {
  const isReassign = mode === 'reassign';
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!visible) return;
    setSelectedVendorId(null);
    setSubmitting(false);
    setError('');
  }, [visible, job]);

  if (!visible || !job) return null;

  const canSubmit = !!selectedVendorId && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await onAssign(selectedVendorId);
    } catch (err) {
      setError(err.response?.data?.message
        || `Failed to ${isReassign ? 're-assign' : 'assign'} job. Please try again.`);
      setSubmitting(false);
    }
  };

  return (
    <div className="ajm-overlay" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="ajm-container">
        <div className="ajm-drag-handle" />
        <div className="ajm-header">
          <span className="ajm-title">{isReassign ? 'Re-Assign Job' : 'Assign Job'}</span>
          <button className="ajm-close-btn" onClick={onClose} disabled={submitting} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <p className="ajm-subtitle">
          {isReassign
            ? <>Choose a different service provider for <strong>{job.address}</strong>.</>
            : <>Choose one of your service providers for <strong>{job.address}</strong>.</>}
        </p>

        <div className="ajm-body">
          {vendorsLoading ? (
            <div className="ajm-state-message">Loading your providers…</div>
          ) : vendorsError ? (
            <div className="ajm-state-message ajm-state-message--error">{vendorsError}</div>
          ) : !vendors || vendors.length === 0 ? (
            <div className="ajm-state-message">No service providers linked to your company yet.</div>
          ) : (
            <div className="ajm-vendor-list">
              {vendors.map((vendor) => {
                const name = [vendor.firstName, vendor.lastName].filter(Boolean).join(' ')
                  || vendor.businessName || vendor.email || 'Unnamed provider';
                const meta = vendor.providerType || (vendor.serviceTypes && vendor.serviceTypes[0]) || 'Service provider';
                const selected = selectedVendorId === vendor.userId;
                return (
                  <div
                    key={vendor.userId}
                    className={`ajm-vendor-row${selected ? ' ajm-vendor-row--selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedVendorId(vendor.userId)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedVendorId(vendor.userId)}
                  >
                    <div className="ajm-vendor-avatar">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    </div>
                    <div className="ajm-vendor-info">
                      <div className="ajm-vendor-name">{name}</div>
                      <div className="ajm-vendor-meta">{meta}</div>
                    </div>
                    <div className={`ajm-vendor-radio${selected ? ' ajm-vendor-radio--selected' : ''}`} />
                  </div>
                );
              })}
            </div>
          )}

          {error && <div className="ajm-error-banner">{error}</div>}
        </div>

        <div className="ajm-footer">
          <button className="ajm-cancel-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="ajm-submit-btn" onClick={handleSubmit} disabled={!canSubmit}>
            {isReassign
              ? (submitting ? 'Re-Assigning…' : 'Re-Assign')
              : (submitting ? 'Assigning…' : 'Assign')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignJobModal;
